import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cx } from "../ui/primitives";
import { MASK, maskIf } from "../../lib/replayMask";

/**
 * The shell every card in the home grid wears: CollectionCard's border, rounding, media
 * area, `p-3` footer and floated badges — with the body wired as an ACTION, not a Link.
 *
 * It exists so the one rule ("clicking plays it; everything else is a button in the
 * footer") is implemented once. A view card and a quick-setup card differ in what they put
 * in the slots, never in how the slots behave.
 */
export function CardShell({
  busy,
  onPlay,
  playLabel,
  media,
  pill,
  badge,
  name,
  icon,
  footerActions,
  blockedUrl,
}: {
  busy: boolean;
  onPlay: () => void;
  playLabel: string;
  media: ReactNode;
  pill?: ReactNode;
  badge: string;
  name: string;
  icon: string;
  footerActions?: ReactNode;
  blockedUrl: string | null;
}) {
  return (
    <div
      // Not a drag source. A saved view's card used to carry an `application/x-gooner-view`
      // payload for dropping onto a screen card in the desktop rail; that rail is gone
      // (screens are driven from the remote alone), and a draggable with nowhere to drop
      // is a gesture that promises what nothing takes. Assignment from a card is the
      // footer's "Send to".
      className="group relative overflow-hidden rounded-xl border border-[var(--color-edge)] transition-colors hover:border-[var(--color-accent)]/50"
    >
      {/*
        The whole card is the primary action, as a button STRETCHED BEHIND the content
        rather than wrapped around it.

        It used to wrap. That forced the secondary actions to be absolutely positioned
        against the card, because a <button> cannot legally contain other buttons — and
        anchoring them to the card is what made them sit at different heights on different
        cards: a card stretched taller by its grid row (the Build View tile sets the row's
        height) moves its own bottom edge without moving its footer, so `bottom-3` landed
        somewhere different depending on what else was in the row.

        Behind the content, the actions can live inside the footer's own flex line and be
        centred by the same `items-center` that centres the name. No offsets to keep in
        sync, and nothing to drift.

        Still a <button> and not a <div role="button">, so Enter and Space work for free;
        still not a link, because the body is an ACTION, not a destination — Edit in the
        footer is the card's one real link.
      */}
      <button
        type="button"
        onClick={onPlay}
        disabled={busy}
        aria-label={playLabel}
        title={playLabel}
        // `playLabel` names the card ("Play <view name>"): masked in replay with its name.
        {...MASK}
        className="absolute inset-0 z-0"
      />

      {/* `pointer-events-none` so every click falls through to the button above — except
          the action row, which takes them back. */}
      <div className={cx("pointer-events-none relative z-10", busy ? "opacity-60" : "")}>
        {/*
          The stage: every card's media area is this one fixed shape, whatever shape the
          view inside it is. A portrait view used to derive its own height from the column
          width and come out twice as tall as its neighbours, which broke the grid's rhythm
          and made "which of these is bigger" read as meaning something it did not.

          The preview inside is `object-contain`, so a view that is not 16:9 is boxed
          against the same black the footer uses — the bars read as the card rather than as
          empty space in it, and a portrait view looks like what it is.
        */}
        <div className="relative flex aspect-video items-center justify-center overflow-hidden bg-black">
          {media}
          {/*
            `z-30` on both corners, and it is load-bearing rather than tidiness.

            A ViewSurface draws each region as a RegionBox stacked at z-10 (up to z-20 when
            selected), so a badge left at `z-index: auto` sits UNDER the regions and
            vanishes on exactly the cards that have media in that corner. It went unnoticed
            because the quick setups escaped it the moment their media became a plain
            <video> with no stacking of its own — so the geometry badge showed on the
            built-ins and silently disappeared from the operator's own views.
          */}
          {pill ? <span className="absolute left-2 top-2 z-30">{pill}</span> : null}
          {/* Geometry in the opposite corner, muted — CollectionCard's clip-count slot. */}
          <span className="absolute bottom-2 right-2 z-30 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-zinc-200">
            {badge}
          </span>
        </div>

        {/* One row: icon, name, and the actions pushed to its end.

            `bg-black` is load-bearing, not decoration: the home screen sits on an ambient
            video backdrop, and without an opaque footer the video plays through the card's
            own name. Fully opaque, and the same black as the media area above it, so the
            card reads as one object rather than a picture with a caption floating over the
            wallpaper. */}
        <div className="flex items-center gap-2 bg-black p-3">
          <span aria-hidden className="shrink-0 text-base leading-none">{icon}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-100" {...MASK}>{name}</span>
          {/* In the flex line, so `items-center` above centres these against the name on
              every card — no `pr-24` reserving a column, and no offset to keep in step
              with the footer's padding. `shrink-0` is what makes the NAME truncate rather
              than the buttons squashing. */}
          {/*
            Revealed on hover, and always present without one.

            Tailwind v4 wraps `hover:` in `@media (hover: hover)` by DEFAULT — so on a touch
            screen `opacity-0` applied and `group-hover:opacity-100` never fired. Edit, Send
            to, Delete and Copy were not merely hard to find on a phone, they were
            unreachable. `pointer-coarse` is the counterpart: a finger gets the buttons, a
            mouse gets the clean card the reveal was designed for.
          */}
          {footerActions ? (
            <div className="pointer-events-auto flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100">
              {footerActions}
            </div>
          ) : null}
        </div>
      </div>

      {blockedUrl ? (
        <a
          href={blockedUrl}
          target="_blank"
          rel="noreferrer"
          className="pointer-events-auto absolute inset-x-2 bottom-12 z-20 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-center text-[11px] text-amber-200 backdrop-blur"
        >
          Your browser blocked the new tab — open the screen
        </a>
      ) : null}
    </div>
  );
}

/** A secondary footer action. Icon-only: these sit below the card body in importance, and
 *  three labelled buttons in a footer row would crowd out the name they sit beside. */
export function FooterAction({
  label,
  danger,
  to,
  onClick,
  mask = false,
  children,
}: {
  label: string;
  danger?: boolean;
  /** The label names something a person wrote ("Delete <view name>"): mask its
   *  aria-label and title in session replay. Icon-only, so nothing visible is lost. */
  mask?: boolean;
  /** A real destination. Renders a Link instead of a button, so middle-click and
   *  open-in-new-tab still work — the card body cannot offer that any more, because it is
   *  an action rather than a link. Same classes either way, so the row stays one row. */
  to?: string;
  onClick?: (anchor: HTMLElement) => void;
  children: ReactNode;
}) {
  const cls = cx(
    "rounded-md bg-black/70 p-1.5 text-zinc-300 transition-colors",
    danger ? "hover:bg-red-500/80 hover:text-white" : "hover:bg-[var(--color-accent)] hover:text-white",
  );
  // Stops the card body underneath from also firing — a click that both edited and played
  // would open a tab nobody asked for.
  const handle = (e: { stopPropagation: () => void; currentTarget: HTMLElement }) => {
    e.stopPropagation();
    onClick?.(e.currentTarget);
  };

  if (to) {
    return (
      <Link to={to} aria-label={label} title={label} className={cls} onClick={handle} {...maskIf(mask)}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" aria-label={label} title={label} onClick={handle} className={cls} {...maskIf(mask)}>
      {children}
    </button>
  );
}
